import React, { useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { useAuth } from '@/auth/clerkShim';
import type { Block, ButtonBlock } from '@staty/shared/schemas/block';
import { newBlockId } from '@staty/shared/utils/blockUtils';
import BlockPalette from './BlockPalette';
import { uploadFile, UploadError } from './uploadImage';

/**
 * Slim hover "+" zone between blocks (and at the list's start/end). Click →
 * palette popover → insert at this index. Kept nearly invisible at rest so
 * the canvas still reads as the public page.
 *
 * The palette's "Document" entry is handled here: pick a PDF, upload it, and
 * insert a Button block pointing at the uploaded URL (label defaults to the
 * file name). The public renderer opens .pdf button links in a new tab.
 */

interface Props {
  leagueName: string;
  onInsert: (block: Block) => void;
  allowColumns?: boolean;
}

// "2027-house-league-rules.pdf" → "2027 house league rules"
function labelFromFileName(name: string): string {
  const base = name.replace(/\.pdf$/i, '').replace(/[-_]+/g, ' ').trim();
  return base || 'Document';
}

export default function BlockInserter({ leagueName, onInsert, allowColumns = true }: Props) {
  const [open, setOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const { getToken } = useAuth();

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    const upload = uploadFile(file, leagueName, getToken).then((fileUrl) => {
      const block: ButtonBlock = {
        id: newBlockId(),
        type: 'button',
        label: labelFromFileName(file.name),
        url: fileUrl,
        // Outline by default: a stack of document buttons should read like a
        // quiet file list (the industry convention), not a column of CTAs.
        // The author can switch any of them to primary in the settings panel.
        style: 'outline',
        alignment: 'left',
      };
      onInsert(block);
    });
    toast.promise(upload, {
      loading: 'Uploading document…',
      success: 'Document added. It appears as a button on the page',
      error: (err: unknown) =>
        err instanceof UploadError ? err.message : 'Failed to upload document',
    });
  };

  return (
    <div className={`canvas-inserter ${open ? 'canvas-inserter--open' : ''}`}>
      <input
        ref={fileInput}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => {
          handleFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="canvas-inserter__button"
            aria-label="Add block"
            title="Add block"
            onClick={(e) => e.stopPropagation()}
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-1" align="center" onClick={(e) => e.stopPropagation()}>
          <BlockPalette
            allowColumns={allowColumns}
            onPick={(block) => {
              setOpen(false);
              onInsert(block);
            }}
            onPickDocument={() => {
              setOpen(false);
              fileInput.current?.click();
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
